import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { getStatusText, printBasicShortDate, URLS, useSocialCoachSession } from "@/utils";
import { CoachInformation } from "../CoachInformation";
import { CoachPermissions } from "../CoachPermissions";
import { VideoRoutingDialog } from "../VideoRoutingDialog";
import { ClientNotesModal } from "@/components/ClientNotesModal";
import { RequestPWModal } from "@/containers/Admin/RequestPWModal";
import { CoachStatistic, CoachStatuses, CompleteAccount } from "@/interfaces";
import { Button, Badge, Dialog, DropdownMenu, Table } from "@/components/ui";
import { cn } from "@/lib/utils";
import copy from "copy-to-clipboard";
import {
  Pencil,
  Settings,
  FileText,
  Calendar,
  Lock,
  Trash2,
  MoreHorizontal,
  AlertTriangle,
  Copy,
  Check,
  HatGlasses,
  Route,
  Files,
  Star,
  Wrench,
} from "lucide-react";

interface OwnProps {
  coachAccount: CompleteAccount;
  coachStats?: CoachStatistic;
  coachIndex: number;

  handlerRefresh: () => void;
  handlerImpersonate: (userGuid: string) => void;
}

const CoachCellFC: React.FC<OwnProps> = ({ coachAccount, coachStats, handlerRefresh, handlerImpersonate }) => {
  const history = useHistory();
  const { profile, user, coach } = coachAccount;
  const [coachDetailsModalOpen, setCoachDetailsModalOpen] = React.useState<boolean>(false);
  const [coachPermissionsModalOpen, setCoachPermissionsModalOpen] = React.useState<boolean>(false);
  const [videoRoutingModalOpen, setVideoRoutingModalOpen] = React.useState<boolean>(false);
  const [notesModal, setNotesModal] = React.useState<boolean>(false);
  const [pwModal, setPWModal] = React.useState<boolean>(false);

  const [copiedId, setCopiedId] = React.useState(false);
  const [copiedEmail, setCopiedEmail] = React.useState(false);

  const handleCopyId = () => {
    if (!coach?.id) return;
    copy(coach.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 1500);
  };

  const handleCopyEmail = () => {
    if (!user?.email) return;
    copy(user.email);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 1500);
  };

  const tags: string[] = useMemo(() => {
    const list = [];
    const groupStrings = user.groups.map(g => g.toString());
    if (groupStrings.includes("ADMIN")) {
      list.push("Admin");
    }
    if (groupStrings.includes("EDITOR")) {
      list.push("Editor");
    }
    if (user.isDemo) {
      list.push("Demo");
    }
    return list;
  }, [user.groups, user.isDemo]);

  const coachModal = () => {
    return (
      <Dialog open={coachDetailsModalOpen} onOpenChange={setCoachDetailsModalOpen} size="xl" showCloseButton>
        <Dialog.Header>
          <Dialog.Title>Coach Information</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content className="max-h-[80vh] overflow-y-auto">
          <CoachInformation
            profile={profile!!}
            user={user}
            coach={coach!!}
            handleUpdateSuccess={() => handlerRefresh()}
          />
        </Dialog.Content>
      </Dialog>
    );
  };

  const actions = useMemo(() => {
    const list = [
      {
        key: "edit",
        content: "Edit",
      },
      {
        key: "settings",
        content: "Coach Settings",
      },
      {
        key: "routing",
        content: "Video Routing",
      },
      {
        key: "notes",
        content: "Edit Notes",
      },
      {
        key: "calendar",
        content: "Calendar",
      },
      {
        key: "tools",
        content: "Tools",
      },
      {
        key: "pw",
        content: "Generate Password Link",
      },
      {
        key: "impersonate",
        content: "Impersonate",
      },
    ];

    return list;
  }, []);

  const handleAction = (index: number): void => {
    const action = actions[index];
    switch (action.key) {
      case "edit":
        return setCoachDetailsModalOpen(true);
      case "settings":
        return setCoachPermissionsModalOpen(true);
      case "routing":
        return setVideoRoutingModalOpen(true);
      case "notes":
        return setNotesModal(true);
      case "calendar":
        return redirectToCalendar();
      case "pw":
        return setPWModal(true);
      case "impersonate":
        return handlerImpersonate(user.guid!);
    }
  };

  const report = useMemo(() => coachStats?.featureEngagementReport, [coachStats?.featureEngagementReport]);

  const diffLabel = (now?: number, prev?: number): React.ReactNode => {
    if (now !== undefined && prev !== undefined) {
      const diff = now - prev;
      if (diff > 0) {
        return (
          <div className="flex flex-col items-center justify-center gap-0.5">
            <span className="font-medium text-foreground text-sm leading-none">{now}</span>
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100/40 text-[10px] font-bold leading-none mt-0.5">
              +{diff}
            </span>
          </div>
        );
      } else if (diff < 0) {
        return (
          <div className="flex flex-col items-center justify-center gap-0.5">
            <span className="font-medium text-foreground text-sm leading-none">{now}</span>
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-100/40 text-[10px] font-bold leading-none mt-0.5">
              {diff}
            </span>
          </div>
        );
      }
      return (
        <div className="flex flex-col items-center justify-center">
          <span className="font-medium text-foreground text-sm">{now}</span>
          <span className="text-[10px] text-muted-foreground/60 font-medium leading-none mt-1">0</span>
        </div>
      );
    } else if (now !== undefined) {
      return <span className="font-medium text-foreground text-sm">{now}</span>;
    } else {
      return <span className="text-muted-foreground text-xs">—</span>;
    }
  };

  const redirectToCalendar = () => {
    window.open(URLS.admin.coachCalendar.replace(":coachId", coach!.id));
  };

  return (
    <Table.Row className="hover:bg-slate-50/40 transition-colors border-b border-slate-100 duration-150">
      <Table.Cell className="px-3 py-2">
        <div className="flex flex-col gap-1">
          {/* ID Badge & Company Name at the top */}
          <div className="flex items-center gap-1.5 flex-wrap leading-none">
            <span className="text-sm font-bold text-slate-800 tracking-tight">
              {coach?.companyName ? (
                coach.companyName
              ) : (
                <span className="italic text-slate-300 font-medium">No Company Name</span>
              )}
            </span>
            <span className="text-slate-300">•</span>
            <span
              onClick={handleCopyId}
              className={cn(
                "text-[9.5px] font-mono font-semibold text-slate-500 bg-slate-50 border border-slate-200/60 px-1 rounded cursor-pointer hover:text-slate-800 flex items-center gap-0.5 transition-colors select-none",
                copiedId && "text-green-600 hover:text-green-600 border-green-200 bg-green-50/40"
              )}
              title="Click to copy ID"
            >
              {coach?.id}
              {copiedId ? (
                <Check className="size-2.5 text-green-500 shrink-0" />
              ) : (
                <Copy className="size-2.5 opacity-40 hover:opacity-100 shrink-0 transition-opacity ml-0.5" />
              )}
            </span>
          </div>

          {/* Coach Name */}
          <div className="font-semibold text-sm text-slate-700 leading-none">
            {profile?.firstName} {profile?.lastName}
          </div>

          {/* Coach Email */}
          <div
            onClick={handleCopyEmail}
            className={cn(
              "text-xs text-slate-500 leading-none flex items-center gap-0.5 cursor-pointer hover:text-slate-800 transition-colors select-none w-max",
              copiedEmail && "text-green-600 hover:text-green-600"
            )}
            title="Click to copy email"
          >
            <span>{user.email}</span>
            {copiedEmail ? (
              <Check className="size-2.5 text-green-500 shrink-0" />
            ) : (
              <Copy className="size-2.5 opacity-40 hover:opacity-100 shrink-0 transition-opacity ml-0.5" />
            )}
          </div>

          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-0.5">
              {tags.map(tag => (
                <Badge
                  key={`coach-tag-${tag}-${coach?.id}`}
                  variant="outline"
                  className="text-[10px] px-1.5 py-0.5 bg-slate-50/50 border-slate-200/40 rounded text-slate-500 font-semibold"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </Table.Cell>

      <Table.Cell className="p-2">
        <div className="flex flex-col gap-0.5 text-xs">
          <div className="font-medium text-foreground">
            {coach?.coachStatus === CoachStatuses.ACTIVE ? (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200/40">
                <span className="size-1 rounded-full bg-emerald-500 shrink-0" />
                {getStatusText(coach?.coachStatus!)}
              </span>
            ) : coach?.coachStatus === CoachStatuses.INACTIVE ? (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-wide bg-slate-50 text-slate-600 border border-slate-200/40">
                <span className="size-1 rounded-full bg-slate-400 shrink-0" />
                {getStatusText(coach?.coachStatus!)}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200/40">
                <span className="size-1 rounded-full bg-amber-500 shrink-0" />
                {getStatusText(coach?.coachStatus!)}
              </span>
            )}
          </div>
        </div>
      </Table.Cell>
      <Table.Cell className="p-2">
        <div className="text-slate-400 text-[11px]">
          <span className="font-semibold text-slate-600">{printBasicShortDate(user?.latestLoginDate)}</span>
        </div>
      </Table.Cell>

      {/* players */}
      <Table.Cell className="text-center font-semibold text-sm p-1 border-l border-slate-100 bg-slate-50/5">
        {report?.totalPlayers ?? 0}
      </Table.Cell>
      <Table.Cell className="text-center font-semibold text-sm p-1 bg-slate-50/5">
        {report?.activePlayers ?? 0}
      </Table.Cell>
      <Table.Cell className="text-center font-semibold text-sm p-1 bg-slate-50/5">
        {report?.enrolledPlayers ?? 0}
      </Table.Cell>
      <Table.Cell className="text-center font-semibold text-sm p-1 bg-slate-50/5">
        {report?.scPlusPlayers ?? 0}
      </Table.Cell>

      {/* prompts */}
      <Table.Cell className="text-center font-semibold text-sm p-1 border-l border-slate-100 bg-slate-50/5">
        {report?.totalPrompts ?? 0}
      </Table.Cell>
      <Table.Cell className="text-center p-2 bg-slate-50/5">
        {diffLabel(report?.prompts90, report?.promptsPrev90)}
      </Table.Cell>
      <Table.Cell className="text-center font-semibold text-sm p-1 bg-slate-50/5">
        {report?.remainingPrompts ?? 0}
      </Table.Cell>

      {/* feature use */}
      <Table.Cell className="text-center p-1 border-l border-slate-100 bg-slate-50/5">
        {diffLabel(report?.posts90, report?.postsPrev90)}
      </Table.Cell>
      <Table.Cell className="text-center p-1 bg-slate-50/5">
        {diffLabel(report?.projects90, report?.projectsPrev90)}
      </Table.Cell>
      <Table.Cell className="text-center p-1 bg-slate-50/5">
        {diffLabel(report?.pages90, report?.pagesPrev90)}
      </Table.Cell>

      <Table.Cell className="p-2 text-right border-l border-slate-100">
        <div className="flex flex-col items-center justify-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 hover:bg-slate-100 p-0 cursor-pointer outline-none"
            onClick={() => handlerImpersonate(user.guid!)}
            title="Impersonate"
          >
            <HatGlasses className="size-4 shrink-0 text-muted-foreground" />
          </Button>
          <DropdownMenu>
            <DropdownMenu.Trigger
              render={
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-slate-100 p-0 cursor-pointer outline-none"
                >
                  <MoreHorizontal className="size-4 shrink-0 text-muted-foreground" />
                </Button>
              }
            />
            <DropdownMenu.Content align="end" className="w-52 border border-slate-100 shadow-md z-5000">
              {actions.map((act, idx) => {
                if (act.key === "tools") {
                  return (
                    <DropdownMenu.Sub key="tools">
                      <DropdownMenu.SubTrigger className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold cursor-pointer">
                        <Wrench className="size-3.5 shrink-0 text-muted-foreground" />
                        Tools
                      </DropdownMenu.SubTrigger>
                      <DropdownMenu.SubContent side="left" className="w-48">
                        <DropdownMenu.Item
                          onClick={() => {
                            if (!user.userId) return;
                            history.push(`${URLS.admin.tools}?userId=${user.userId}`);
                          }}
                          className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold cursor-pointer"
                        >
                          <Files className="size-3.5 shrink-0 text-muted-foreground" />
                          Uploaded files
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                          onClick={() => {
                            if (!coach?.id) return;
                            history.push(`${URLS.admin.tools}?tab=review-uploads&coachId=${coach.id}`);
                          }}
                          className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold cursor-pointer"
                        >
                          <Star className="size-3.5 shrink-0 text-muted-foreground" />
                          Review uploads
                        </DropdownMenu.Item>
                      </DropdownMenu.SubContent>
                    </DropdownMenu.Sub>
                  );
                }
                const IconComponent = (() => {
                  switch (act.key) {
                    case "edit":
                      return Pencil;
                    case "settings":
                      return Settings;
                    case "routing":
                      return Route;
                    case "notes":
                      return FileText;
                    case "calendar":
                      return Calendar;
                    case "pw":
                      return Lock;
                    case "impersonate":
                      return HatGlasses;
                    default:
                      return MoreHorizontal;
                  }
                })();
                return (
                  <DropdownMenu.Item
                    key={act.key}
                    onClick={() => handleAction(idx)}
                    variant={"default"}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold cursor-pointer"
                  >
                    <IconComponent className={cn("size-3.5 shrink-0", "text-muted-foreground")} />
                    <span>{act.content}</span>
                  </DropdownMenu.Item>
                );
              })}
            </DropdownMenu.Content>
          </DropdownMenu>
        </div>
      </Table.Cell>

      {coachModal()}
      {coachPermissionsModalOpen && (
        <CoachPermissions
          opened={coachPermissionsModalOpen}
          coach={coach!!}
          handleUpdateSuccess={() => {
            setCoachPermissionsModalOpen(false);
          }}
        />
      )}
      {videoRoutingModalOpen && coach?.id && (
        <VideoRoutingDialog
          open={videoRoutingModalOpen}
          onOpenChange={setVideoRoutingModalOpen}
          coachId={Number(coach.id)}
          coachName={
            coach.companyName ||
            [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") ||
            user.email ||
            `Coach ${coach.id}`
          }
        />
      )}
      {notesModal && <ClientNotesModal open={notesModal} coachId={coach?.id} onClose={() => setNotesModal(false)} />}
      {pwModal && user.email && (
        <RequestPWModal open={pwModal} onClose={() => setPWModal(false)} targetEmail={user.email} />
      )}
    </Table.Row>
  );
};

export const CoachCell = CoachCellFC;
